import { IAllergy, IPatient, NEW_ALLERGY_ID } from '../caremap/ICareMap';
import { SupportGroupDataProvider } from '../caremap/SupportGroupDataProvider';
import { LabelProvider } from '../config/LabelProvider';
import { DuoHandDotsIcon } from '../theme/Icons';
import { AllergySelectField } from './AllergySelectField';
import { IStructuredFieldDef, IStructuredRecordDescriptor, IStructuredRecordIsPublicKey, IStructuredRecordPatientKey } from './structured-records/StructuredRecordDescriptor';

export class AllergyDescriptor implements IStructuredRecordDescriptor<IAllergy> {
    static inject = () => [SupportGroupDataProvider, LabelProvider];

    constructor(
        private dataProvider: SupportGroupDataProvider,
        private labelProvider: LabelProvider
    ) {}

    patientKey: IStructuredRecordPatientKey = 'allergies';
    idField: keyof IAllergy = 'id';
    newId = NEW_ALLERGY_ID;
    mode = 'edit-allergy';
    routeSegment = 'allergies';
    singular = 'Allergy';
    plural = 'Allergies';
    Icon = DuoHandDotsIcon;
    isPublicKey: IStructuredRecordIsPublicKey = 'allergiesIsPublic';

    trackingNoun = 'Allergy';

    getName = (allergy: IAllergy) => allergy.allergy;

    buildTrackingProps = (allergy: IAllergy) => ({
        'Has Reaction': Boolean(allergy.reaction),
        'Severity': allergy.severity,
        'Has Notes': Boolean(allergy.notes)
    });

    getRecords = (patient: IPatient) => patient.allergies;
    withRecords = (patient: IPatient, records: IAllergy[]): IPatient => ({ ...patient, allergies: records });
    createBlank = (id: string): IAllergy => ({ id });

    // a getter (not a stored field) so the allergy-name label re-reads LabelProvider's
    // use_MY_CAREMAP__PrefixIfApplicable flag on every access. That flag flips per active caremap
    // (see CareMapState, which is the only writer), but this descriptor is a long-lived IoC
    // singleton -- a one-time-computed label would freeze at whichever caremap was active when
    // the container first constructed this instance, and never update again as the user switches
    // care recipients.
    get fields(): IStructuredFieldDef<IAllergy>[] {
        const label = this.labelProvider.getLabel('CARE_RECIPIENT_ALLERGY');

        return [
            {
                key: 'allergy',
                valueKey: 'allergyId',
                label,
                type: 'searchable-select',
                required: true,
                getSelectOptions: () => this.dataProvider.getAllergySelectOptions().map(option => ({ id: option.value, label: option.label })),
                // reuse AllergySelectField verbatim -- it writes the allergyId/allergy pair AND
                // fires the 'Custom Type Added' metric for free-typed values, neither of which the
                // generic searchable-select atom above knows how to do.
                render: ({ record, readOnly, patch }) => (
                    <AllergySelectField
                        label={label}
                        allergyId={record.allergyId}
                        allergy={record.allergy}
                        onChange={d => patch(d)}
                        readOnly={readOnly}
                    />
                )
            },
            {
                key: 'reaction',
                label: 'Reaction',
                type: 'textarea'
            },
            {
                key: 'severity',
                label: 'Severity',
                type: 'enum-select',
                enumOptions: [
                    { value: 'mild', label: 'Mild' },
                    { value: 'moderate', label: 'Moderate' },
                    { value: 'severe', label: 'Severe' }
                ]
            },
            {
                key: 'notes',
                label: 'Notes',
                type: 'textarea'
            }
        ];
    }
}
